import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * TocRail — right-rail table of contents on docs site article pages.
 *
 * Design plan section reference,
 * section 22.3 (Article page mockup) and section 28.3 (heading conventions).
 *
 * Responsibilities — kept narrow:
 *   - Render an indented list of headings (H2 and H3 only; the docs IA
 *     contract caps nav depth at 3, so no H4 entries by construction).
 *   - Highlight the currently-active heading via a brand-green left bar
 *     (same visual rhythm as Sidebar active rows for consistency).
 *   - Fire `onSelect(id)` on click. Caller wires the actual scroll-to-
 *     anchor + URL hash update.
 *
 * What this component intentionally does NOT do:
 *   - It does not run scroll-spy. The companion `useTocScrollSpy` hook
 *     (same file) is opt-in; consumers that want a different active-id
 *     source (URL hash, manual state, server-set) pass their own value.
 *   - It does not render the "Was this helpful?" widget — that's a
 *     separate Track D component (WasThisHelpful) so authors can
 *     compose them independently on article + reference pages.
 */

export interface TocEntry {
  /** Heading id (the `id` attribute on the H2/H3 in the rendered article). */
  id: string
  /** Display text. */
  text: string
  /** 2 for H2, 3 for H3. Max depth is 3 per the docs IA contract. */
  depth: 2 | 3
}

export interface TocRailProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onSelect'> {
  /** Flat list of headings in document order. */
  entries: TocEntry[]
  /** Currently-visible heading id. Caller drives this — see useTocScrollSpy. */
  activeId?: string
  /**
   * Called when the user clicks an entry. Caller smooths-scrolls to
   * the anchor and updates the URL hash. Default click navigates via
   * native `<a href="#id">` so the rail works without JS too.
   */
  onSelect?: (id: string) => void
  /** Overline label. Default 'On this page'. */
  label?: string
}

export const TocRail = React.forwardRef<HTMLElement, TocRailProps>(
  ({ entries, activeId, onSelect, label = 'On this page', className, ...rest }, ref) => {
    if (entries.length === 0) {
      // Empty TOC: render nothing. Article pages without H2/H3 (short
      // concept topics) hide the rail rather than show an empty kicker.
      return null
    }

    return (
      <nav
        ref={ref}
        aria-label={label}
        className={cn('flex flex-col gap-2 w-[200px] text-body-sm', className)}
        {...rest}
      >
        {/* Overline label. Uses text-ia-text-secondary (not -tertiary)
            because -tertiary fails WCAG AA on the light page bg even at
            9px semibold. The uppercase + tracking + small font already
            establish the "label" visual register without needing a
            lighter color. */}
        <div className="font-mono text-[9px] font-semibold uppercase tracking-[1.2px] text-ia-text-secondary px-3">
          {label}
        </div>
        <ol className="flex flex-col gap-[1px] list-none m-0 p-0">
          {entries.map((entry) => {
            const isActive = entry.id === activeId
            const indentPx = entry.depth === 3 ? 12 : 0
            return (
              <li key={entry.id} className="m-0">
                <a
                  href={`#${entry.id}`}
                  onClick={(ev) => {
                    if (onSelect) {
                      ev.preventDefault()
                      onSelect(entry.id)
                    }
                  }}
                  aria-current={isActive ? 'location' : undefined}
                  style={{ paddingLeft: 12 + indentPx + (isActive ? -3 : 0) }}
                  className={cn(
                    'relative block py-1.5 pr-2 rounded-md transition-colors duration-100 motion-reduce:transition-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
                    isActive
                      ? [
                          'border-l-[3px] border-[var(--color-ia-sidebar-active-border)]',
                          'text-ia-green-text font-semibold',
                        ].join(' ')
                      : // Visual hierarchy between H2 and H3 is carried by
                        // the 12px indent on H3 + the active highlight on
                        // the current section. Both depths use the same
                        // text-ia-text-secondary token so contrast meets AA
                        // (text-ia-text-tertiary fails at 13px on the light
                        // page bg). Indent + active state still read clearly.
                        'text-ia-text-secondary hover:text-ia-text',
                  )}
                >
                  {entry.text}
                </a>
              </li>
            )
          })}
        </ol>
      </nav>
    )
  },
)
TocRail.displayName = 'TocRail'

/**
 * useTocScrollSpy — watches the rendered article's headings via
 * IntersectionObserver and returns the id of the heading nearest the
 * top of the viewport. Pair with TocRail's `activeId` prop.
 *
 * Usage:
 *
 *   const entries = [...]   // derived from the rendered Markdoc body
 *   const active = useTocScrollSpy(entries)
 *   <TocRail entries={entries} activeId={active} />
 *
 * Caller must ensure each entry id matches an `id=...` attribute on a
 * heading element in the document. Astro's autolink rehype plugin
 * does this automatically.
 *
 * Options:
 *   - rootMargin: how far above the viewport edge a heading must reach
 *     before it becomes "active". Default '-80px 0px -75% 0px' biases
 *     the active heading toward the top quarter of the viewport, which
 *     reads better than naive "first visible" for long articles.
 */
export function useTocScrollSpy(
  entries: TocEntry[],
  opts: { rootMargin?: string } = {},
): string | undefined {
  const { rootMargin = '-80px 0px -75% 0px' } = opts
  const [active, setActive] = React.useState<string | undefined>(entries[0]?.id)

  React.useEffect(() => {
    if (typeof window === 'undefined' || entries.length === 0) return
    if (typeof IntersectionObserver === 'undefined') return

    // Map id -> the actual heading element. Resolve once at mount.
    const elementById = new Map<string, Element>()
    for (const entry of entries) {
      const el = document.getElementById(entry.id)
      if (el) elementById.set(entry.id, el)
    }
    if (elementById.size === 0) return

    // Track which ids are currently intersecting the spy band.
    const intersecting = new Set<string>()

    const observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          const id = record.target.id
          if (record.isIntersecting) intersecting.add(id)
          else intersecting.delete(id)
        }
        // Pick the intersecting id whose entry appears earliest in
        // document order — the highest-up heading wins. Fallback to
        // the existing active if none intersect right now (during
        // micro-scrolls between sections).
        for (const entry of entries) {
          if (intersecting.has(entry.id)) {
            setActive(entry.id)
            return
          }
        }
      },
      { rootMargin, threshold: 0 },
    )

    for (const el of elementById.values()) observer.observe(el)
    return () => observer.disconnect()
  }, [entries, rootMargin])

  return active
}
